import React, { useEffect, useState } from 'react';
import { Backdrop, Breadcrumbs, Button, Grid, IconButton, Typography } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { safeGet, safePost } from '../axios-default';
import { easySnackbar } from '../store/Noti/noti.actions';
import RestorationFormFields, { RESTORATION_FORM_DEFAULT_VALUES } from './SmileDesign/RestorationFormFields';
import AlertDialog from '../components/Dialog/Dialog';
import LoadingSpinner from '../components/Loader/LoadingSpinner';
import style from './MockupOrdering.module.css';
import newPatientTabsStyle from '../components/NewPatient/NewPatientsTabs.module.css';
import newPatientButtonStyle from '../components/NewPatient/NewPatientButtonTabs.module.css';
import { useLang } from '../util/lang';

const MOCKUP_ORDERING_STEPS = [{ id: 'mockup', label: 'Mock-up', Icon: AssignmentIcon, isInvalid: false }];

const validationSchema = yup.object({
    restorationType: yup.string().required('This field is required'),
    biofunctionalMockup: yup.string().required('This field is required'),
    materialShade: yup.string().when('biofunctionalMockup', {
        is: 'mockupKeyAndMaterial',
        then: () => yup.string().required('This field is required'),
    }),
    gingivectomyGuide: yup.string().required('This field is required'),
});

// Renders the Mock-up Ordering page.
const MockupOrdering = () => {
    const get = useLang();
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { id: caseId } = useParams();
    const patientId = location.state?.patientId || caseId;
    const isMarieGroup = location.state?.isMarieGroup;
    const isSEAGroup = JSON.parse(localStorage.getItem('isSEAGroup')) || false;
    const methods = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        defaultValues: RESTORATION_FORM_DEFAULT_VALUES,
    });
    const { setValue } = methods;
    const lastTimeOpenCaseId = useSelector((state) => state.newCaseReducer.lastTimeOpenCaseId);
    const [activeStep, setActiveStep] = useState(MOCKUP_ORDERING_STEPS[0].id);
    const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /** Returns to the last opened patient or Case Management. */
    const handleBack = () => {
        if (caseId !== null && caseId !== undefined) {
            history.push(`/patients/${caseId}`);
            return;
        }

        history.push('/');
    };

    /** Switches the visible Mock-up Ordering step. */
    const handleStepChange = (stepId) => {
        setActiveStep(stepId);
    };

    /** Submits the validated Mock-up Ordering form. */
    const handleSave = async (data) => {
        if (isSubmitting) return;

        const mockupOrder = {
            restorationType: data.restorationType,
            biofunctionalMockupOrder: `option.${data.biofunctionalMockup}`,
            materialShade: data.materialShade,
            gingivectomyGuide: `option.${data.gingivectomyGuide}`,
            remark: data.restorationRemark,
        };

        let shouldRedirect = false;
        setIsSubmitting(true);
        try {
            const result = await safePost(`case/mockup_order/${caseId}`, mockupOrder);
            if (result?.success) {
                shouldRedirect = true;
                return;
            }

            dispatch(easySnackbar(result?.message || 'Failed to submit mock-up order', 'error'));
        } catch (error) {
            dispatch(easySnackbar(error?.response?.data?.message || error?.message || 'Failed to submit mock-up order', 'error'));
        } finally {
            setIsSubmitting(false);
            if (shouldRedirect) {
                history.push(`/patients/${caseId}`);
            }
        }
    };

    /** Opens the required-fields dialog when validation fails. */
    const handleInvalidSave = () => {
        setIsValidationDialogOpen(true);
    };

    useEffect(() => {
        /** Loads the Prescription Note Q1 value for the current case. */
        async function loadDefaultRestorationType() {
            const response = await safeGet(`/case/smileDesignPrescriptionNotes/${caseId}`);
            setValue('restorationType', response?.result?.plannedRestorationSolution || '', { shouldValidate: true });
        }

        loadDefaultRestorationType();
    }, [caseId, setValue]);

    return (
        <FormProvider {...methods}>
            <div className="mockup-ordering-radio-scope" style={{ padding: '24px 32px'}}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} style={{ marginBottom: 16 }}>
                    <Link to="/" style={{ color: 'black' }}>Case Management</Link>
                    <Link to={`/patients/${caseId}`} style={{ color: 'black' }}>{`Patient #${patientId}`}</Link>
                    <Typography>{get('@PatientInfo.smileDesign.mockupOrdering.title')}</Typography>
                </Breadcrumbs>

                <Button variant="contained" className={style.back} onClick={handleBack}>
                    Back
                </Button>

                <div style={{ display: 'flex', flexDirection: 'column'}}>
                    <div className={style.steps}>
                        {MOCKUP_ORDERING_STEPS.map((step, index) => {
                            const isActive = activeStep === step.id;
                            const StepIcon = step.Icon;

                            return (
                                <React.Fragment key={step.id}>
                                    <div className={newPatientTabsStyle.iconButtonContainer} onClick={() => handleStepChange(step.id)}>
                                        <div className={newPatientTabsStyle.CustomBadgeContainer}>
                                            {step.isInvalid && <span className={newPatientTabsStyle.CustomBadge}>*</span>}
                                            <IconButton
                                                aria-label={step.label}
                                                style={{ background: isActive ? '#2eafb6' : '#bababa' }}
                                                className={newPatientTabsStyle.iconButton}
                                            >
                                                <StepIcon />
                                            </IconButton>
                                        </div>
                                        <p className={isActive ? style.stepTextActive : style.stepText}>{step.label}</p>
                                    </div>
                                    {index < MOCKUP_ORDERING_STEPS.length - 1 && (
                                        <div className={newPatientTabsStyle.iconButtonArrowContainer}>
                                            <IconButton aria-label="next step" disabled className="iconButtonArrow">
                                                <KeyboardArrowRightIcon />
                                            </IconButton>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <div style={{ flex: 1}}>
                        <div style={{ color: '#04a8b2', fontSize: 22, fontWeight: 600, marginBottom: 2 }}>{get('@PatientInfo.smileDesign.mockupOrdering.title')}</div>
                        <div style={{ color: '#04a8b2', fontSize: 22, fontWeight: 600, marginBottom: 32 }}>{get('@PatientInfo.smileDesign.mockupOrdering.stepTwo')}</div>
                        <Grid container direction="column">
                            <RestorationFormFields
                                isMockupOrdering
                                isMarieGroup={isMarieGroup}
                                isSEAGroup={isSEAGroup}
                            />
                        </Grid>
                        <div className={newPatientButtonStyle.customButton} style={{ overflow: 'hidden' }}>
                            <button
                                type="button"
                                className={newPatientButtonStyle.next}
                                onClick={methods.handleSubmit(handleSave, handleInvalidSave)}
                                disabled={isSubmitting}
                            >
                                {get('@NewPatient.navBtns.submit')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <AlertDialog
                title="Warning"
                dialogOpen={isValidationDialogOpen}
                handleDialogClose={() => setIsValidationDialogOpen(false)}
                content={`Please select the mandatory option.`}
            />
            <Backdrop style={{ zIndex: 9999, color: '#fff' }} open={isSubmitting}>
                <LoadingSpinner />
            </Backdrop>
        </FormProvider>
    );
};

export default MockupOrdering;
